import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContextValue, useAuth } from "../../providers/AuthProvider";
import axiosBaseURL from "..";

export const useContractList = (
  orderId?: string,
  options?: {
    search?: string;
    status?: string;
    page?: number;
    size?: number;
  },
) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "useContractList",
      orderId,
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ],
    staleTime: 120_000,
    enabled: !!orderId && !!user?.token,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("pageNumber", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("pageSize", options.size.toString());
      if (options?.status) queryParams.append("status", options.status);
      if (options?.search) queryParams.append("search", options.search);

      const url = `/operation/orders/${orderId}/contracts?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useContractByOrderIdList = (order_id?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["contracts by order id,", order_id],
    staleTime: 120,
    enabled: !!user?.token,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/operation/order/${order_id}/contracts`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetContractById = (id?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["contracts detail,", id],
    staleTime: 120,
    enabled: !!user?.token,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/contract/${id}/details`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useContractListForOperation = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "get-list-of-contracts",
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/operation/contracts?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
  });
};

export const useContractListByOrderId = (order_id?: string) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["contract-list-by-order-id", order_id],
    staleTime: 120,
    enabled: !!user?.token,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/operation/contracts?orderId=${order_id}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
            // Authorization: authString,
          },
        },
      );

      return result;
    },
  });
};

export const useContractListByUserId = (user_id: string) => {
  return useQuery({
    queryKey: ["contracts by user id", user_id],
    staleTime: 120,
    queryFn: async () => {
      // const result = await supabase.rpc('off_taker_get_my_contracctss', { user_id });

      return "result";
    },
  });
};

export const useUpdateContractStatus = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { status: number; id: number }) => {
      const result = await axiosBaseURL.post(
        `/loan/approveReject/${data.status}/${data.id}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useGetRejectionReasonsByContractById = (id?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["rejection reasons by contract id", id],
    staleTime: 120,
    enabled: !!user?.token,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/contract/${id}/rejectionReasons`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useAddRejectionReason = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: {
      reason: string;
      id: number;
      status: string;
    }) => {
      const result = await axiosBaseURL.post(
        `/contract/${data.id}/rejectionReasons`,
        { ...data },
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

export const useUpdateRejectionStatus = () => {
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (data: { status: boolean; id: number }) => {
      const result = await axiosBaseURL.put(
        `/contract/${data.id}/rejectionReasons/${
          data.status === true ? "ACTIVE" : "RESOLVED"
        }`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};

// Approve or reject loan
export const useUpdateRejectionLoan = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      bankVerification: boolean;
      contractId: number;
      entitlementDetailId: number;
    }) => {
      const result = await axiosBaseURL.post(
        `/loan/approveReject/entitlement`,
        { ...data },
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts detail"] });
    },
  });
};

/**
 * Phase-targeted retry of a failed loan stage. Backend inspects the
 * entitlement's current `LoanStatus` and re-runs only the failed step
 * (loan request, add disbursement account, or disburse). Returns the
 * standard `FEResponse` envelope; callers should branch on `data.status`
 * (`"1"` = ok, `"0"` = backend reported a failure).
 */
export const useRetryLoanForEntitlement = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { entitlementDetailId: number }) => {
      const result = await axiosBaseURL.post(
        `/loan/retry/entitlement/${data.entitlementDetailId}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contracts detail"] });
    },
  });
};

export const useFarmingStagesByContractId = (contractId?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["contract_farming_stage", contractId],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/contract/${contractId}/farming-stage`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        },
      );

      return result;
    },
  });
};
